import {AdditiveBlending, BackSide, DynamicDrawUsage, InstancedBufferAttribute, BoxGeometry, Mesh, MeshLambertMaterial, MultiplyBlending, ShaderMaterial, SphereGeometry} from 'three';
import CarGeometry from './car-geometry.js';
import InstancedGeometry from './instanced-geometry.js';
import LondonTubeCarGeometry, {LONDON_TUBE_MARKER_PROFILE} from './london-tube-car-geometry.js';
import MeshSet from './mesh-set.js';
import {
    getCarGeometryArguments,
    resolveCarDimensions,
    resolveCarModelScales
} from '../helpers/train-marker-visuals.mjs';
import {updateVertexShader, updateFragmentShader, offsetLondonTrainVertexShader} from './shaders.js';
import delayMarkerFragmentShader from './delay-marker-fragment.glsl';
import delayMarkerVertexShader from './delay-marker-vertex.glsl';
import outlineFragmentShader from './outline-fragment.glsl';
import outlineVertexShader from './outline-vertex.glsl';
import pickingFragmentShader from './picking-fragment.glsl';
import pickingVertexShader from './picking-vertex.glsl';

export default class extends MeshSet {

    constructor(count, parameters) {
        super(parameters);

        const me = this,
            isLondonTube = me.markerStyle = parameters.markerStyle === 'london-tube',
            dimensions = me.dimensions = isLondonTube ? {
                width: LONDON_TUBE_MARKER_PROFILE.width,
                height: LONDON_TUBE_MARKER_PROFILE.totalLength,
                depth: LONDON_TUBE_MARKER_PROFILE.height
            } : resolveCarDimensions(parameters.dimensions),
            geometryArguments = getCarGeometryArguments(dimensions),
            scales = resolveCarModelScales(parameters);

        me.independentDelayMarkerScale = scales.independentDelayMarkerScale;
        me.delayMarkerModelScale = {value: scales.delayMarkerModelScale};

        me.uniforms.outlinePadding = {
            value: isLondonTube ? LONDON_TUBE_MARKER_PROFILE.outlinePadding : 0.1
        };

        const carGeometry = isLondonTube ? new LondonTubeCarGeometry() : new CarGeometry(...geometryArguments);
        const geometry = me.geometry = new InstancedGeometry(carGeometry, count);

        carGeometry.dispose();

        Object.assign(me.uniforms, {
            textureData0: {value: null},
            textureData1: {value: null},
            textureColor: {value: null}
        });

        const material = me.material = new MeshLambertMaterial({
            transparent: true
        });

        material.onBeforeCompile = shader => {
            Object.assign(shader.uniforms, me.getUniforms());
            shader.vertexShader = updateVertexShader(shader.vertexShader);
            if (isLondonTube) shader.vertexShader = offsetLondonTrainVertexShader(shader.vertexShader);
            shader.fragmentShader = updateFragmentShader(shader.fragmentShader);
            shader.defines = {
                CAR: true,
                ...(isLondonTube ? {LONDON_TUBE: true} : {})
            };
        };

        const mesh = me.mesh = new Mesh(geometry, material);

        mesh.updateMatrix();
        mesh.matrixAutoUpdate = false;
        mesh.frustumCulled = false;

        const pickingMaterial = me.pickingMaterial = new ShaderMaterial({
            uniforms: me.getUniforms(),
            vertexShader: isLondonTube ? offsetLondonTrainVertexShader(pickingVertexShader) : pickingVertexShader,
            fragmentShader: pickingFragmentShader,
            defines: {CAR: true}
        });

        const pickingMesh = me.pickingMesh = new Mesh(geometry, pickingMaterial);

        pickingMesh.updateMatrix();
        pickingMesh.matrixAutoUpdate = false;
        pickingMesh.frustumCulled = false;

        const sphereGeometry = new SphereGeometry(1.8, 32, 32);
        const delayMarkerGeometry = me.delayMarkerGeometry = new InstancedGeometry(sphereGeometry, count);

        sphereGeometry.dispose();

        const delayMarkerMaterial = me.delayMarkerMaterial = new ShaderMaterial({
            uniforms: {
                ...me.getUniforms(),
                modelScale: me.delayMarkerModelScale,
                base: {value: 1}
            },
            vertexShader: isLondonTube ? offsetLondonTrainVertexShader(delayMarkerVertexShader) : delayMarkerVertexShader,
            fragmentShader: delayMarkerFragmentShader,
            blending: MultiplyBlending,
            depthWrite: false
        });

        const delayMarkerMesh = me.delayMarkerMesh = new Mesh(delayMarkerGeometry, delayMarkerMaterial);

        delayMarkerMesh.updateMatrix();
        delayMarkerMesh.matrixAutoUpdate = false;
        delayMarkerMesh.frustumCulled = false;

        const outlineArguments = isLondonTube ? [
            LONDON_TUBE_MARKER_PROFILE.outlineWidth,
            LONDON_TUBE_MARKER_PROFILE.outlineLength,
            LONDON_TUBE_MARKER_PROFILE.outlineHeight
        ] : geometryArguments;
        const boxGeometry = new BoxGeometry(...outlineArguments);
        const outlineGeometry = me.outlineGeometry = new InstancedGeometry(boxGeometry, 2);

        boxGeometry.dispose();

        const outlineMaterial = me.outlineMaterial = new ShaderMaterial({
            uniforms: {
                ...me.getUniforms(),
                marked: {value: -1},
                tracked: {value: -1},
                intensity: {value: 0}
            },
            vertexShader: isLondonTube ? offsetLondonTrainVertexShader(outlineVertexShader) : outlineVertexShader,
            fragmentShader: outlineFragmentShader,
            defines: {CAR: true},
            transparent: true,
            side: BackSide
        });

        const outlineMesh = me.outlineMesh = new Mesh(outlineGeometry, outlineMaterial);

        outlineMesh.updateMatrix();
        outlineMesh.matrixAutoUpdate = false;
        outlineMesh.frustumCulled = false;

        if (isLondonTube) {
            for (const target of [geometry, outlineGeometry, delayMarkerGeometry]) {
                target.setAttribute('londonOffset', new InstancedBufferAttribute(
                    new Float32Array(target.getAttribute('instanceID').count * 2), 2
                ).setUsage(DynamicDrawUsage));
            }
        }
    }

    setVisualOffsets(offsets, width, height) {
        if (!this.markerStyle) return;
        for (const geometry of [this.geometry, this.outlineGeometry, this.delayMarkerGeometry]) {
            const ids = geometry.getAttribute('instanceID').array;
            const attribute = geometry.getAttribute('londonOffset');
            for (let i = 0; i < geometry.instanceCount; i++) {
                const offset = offsets.get(ids[i]);
                attribute.array[i * 2] = offset ? offset.x * 2 / width : 0;
                attribute.array[i * 2 + 1] = offset ? -offset.y * 2 / height : 0;
            }
            attribute.addUpdateRange(0, geometry.instanceCount * 2);
            attribute.needsUpdate = true;
        }
    }

    refreshCameraParams(params) {
        const me = this;

        super.refreshCameraParams(params);
        if (params.delayMarkerModelScale !== undefined) {
            me.delayMarkerModelScale.value = params.delayMarkerModelScale;
        } else if (!me.independentDelayMarkerScale && params.modelScale !== undefined) {
            me.delayMarkerModelScale.value = params.modelScale;
        }
    }

    setTextures(textures) {
        const me = this,
            uniforms = me.uniforms,
            p = performance.now() % 1500 / 1500 * 2.0;

        uniforms.textureData0.value = textures[0];
        uniforms.textureData1.value = textures[1];
        uniforms.textureColor.value = textures[2];

        me.outlineMaterial.uniforms.intensity.value = p < 1 ? p : 2 - p;
    }

    setInstanceIDs(ids) {
        const me = this;

        me.geometry.setInstanceIDs(ids.body);
        me.delayMarkerGeometry.setInstanceIDs(ids.delayMarker);
        me.outlineGeometry.setInstanceIDs(ids.outline);
    }

    setMarkedInstanceID(id) {
        this.outlineMaterial.uniforms.marked.value = id;
    }

    setTrackedInstanceID(id) {
        this.outlineMaterial.uniforms.tracked.value = id;
    }

    getUniforms() {
        return this.uniforms;
    }

    getDelayMarkerMesh() {
        return this.delayMarkerMesh;
    }

    refreshDelayMarkerMesh(dark) {
        const me = this;

        me.delayMarkerMaterial.uniforms.base.value = dark ? 0 : 1;
        me.delayMarkerMesh.material.blending = dark ? AdditiveBlending : MultiplyBlending;
    }

    dispose() {
        const me = this;

        me.geometry.dispose();
        me.material.dispose();
        me.pickingMaterial.dispose();
        me.delayMarkerGeometry.dispose();
        me.delayMarkerMaterial.dispose();
        me.outlineGeometry.dispose();
        me.outlineMaterial.dispose();
    }

}
